/**
 * Client-side document extraction for PDF, DOCX, PPTX, and XLSX files.
 * Extracts text per page/slide/sheet AND embedded images for Vision analysis.
 */

import * as pdfjs from 'pdfjs-dist';
import mammoth from 'mammoth';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';

// ---------------------------------------------------------------------------
// pdfjs worker setup – the installed version's own worker, served by the app
// (static/vendor/pdfjs/<version>/, R2) instead of a CDN. The path carries the
// version, so upgrading pdfjs-dist without copying its worker fails a test
// rather than a customer's upload. `.js`, not `.mjs`: every server sends it as
// JavaScript, which a module worker requires.
// ---------------------------------------------------------------------------
pdfjs.GlobalWorkerOptions.workerSrc = `/vendor/pdfjs/${pdfjs.version}/pdf.worker.min.js`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ExtractedImage {
  /** Base64-encoded image data (no data: prefix) */
  data: string;
  mediaType: 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp';
  /** Context hint: which page/slide */
  context: string;
  /** Pixel dimensions (0 if unknown before resize pass) */
  width: number;
  height: number;
}

export interface ParsedPage {
  pageNumber: number;
  text: string;
  /** Extracted images from this page/slide (if any) */
  images?: ExtractedImage[];
}

export interface ParsedDocument {
  fileName: string;
  fileType: 'pdf' | 'docx' | 'pptx' | 'xlsx' | 'website' | 'gdoc' | 'gsheet' | 'gslide';
  pages: ParsedPage[];
  fullText: string;
  /** Original URL for website sources */
  sourceUrl?: string;
}

export type SupportedFileType = 'pdf' | 'docx' | 'pptx' | 'xlsx';

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB
const MAX_IMAGES_PER_DOC = 10;
const MAX_IMAGE_DIMENSION = 2048; // Claude Vision recommended max
const MIN_IMAGE_DIMENSION = 50; // Skip tiny icons/bullets

const SUPPORTED_IMAGE_EXTENSIONS = /\.(png|jpg|jpeg|gif|webp)$/i;

const MIME_MAP: Record<string, SupportedFileType> = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-excel': 'xlsx',
};

const EXT_MAP: Record<string, SupportedFileType> = {
  '.pdf': 'pdf',
  '.docx': 'docx',
  '.pptx': 'pptx',
  '.xlsx': 'xlsx',
  '.xls': 'xlsx',
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function detectFileType(file: File): SupportedFileType | null {
  const byMime = MIME_MAP[file.type];
  if (byMime) return byMime;
  const ext = file.name.toLowerCase().match(/\.[^.]+$/)?.[0] ?? '';
  return EXT_MAP[ext] ?? null;
}

export function validateFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE) {
    return `File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 20 MB.`;
  }
  if (!detectFileType(file)) {
    return 'Unsupported file type. Please upload a PDF, DOCX, PPTX, or Excel file.';
  }
  return null;
}

export async function parseDocument(file: File): Promise<ParsedDocument> {
  const validationError = validateFile(file);
  if (validationError) throw new Error(validationError);

  const fileType = detectFileType(file)!;
  const buffer = await file.arrayBuffer();

  let pages: ParsedPage[];
  switch (fileType) {
    case 'pdf':
      pages = await parsePdf(buffer);
      break;
    case 'docx':
      pages = await parseDocx(buffer);
      break;
    case 'pptx':
      pages = await parsePptx(buffer);
      break;
    case 'xlsx':
      pages = parseXlsx(buffer);
      break;
  }

  // Enforce per-doc image budget: keep largest, resize oversized, drop tiny
  await enforceImageBudget(pages);

  const fullText = pages.map(p => p.text).join('\n\n');

  return { fileName: file.name, fileType, pages, fullText };
}

// ---------------------------------------------------------------------------
// Shared table formatter — uniform pipe-separated output for all parsers
// ---------------------------------------------------------------------------

const DRAWINGML_NS = 'http://schemas.openxmlformats.org/drawingml/2006/main';

/** Format a 2D rows×cols array into LLM-friendly pipe-separated text. */
function formatTableAsText(rows: string[][]): string {
  if (rows.length === 0) return '';
  const formatted = rows.map(row => row.map(cell => cell.trim() || '(empty)').join(' | '));
  // Insert separator after first row (header)
  if (formatted.length > 1) {
    formatted.splice(1, 0, '---');
  }
  return formatted.join('\n');
}

// ---------------------------------------------------------------------------
// PDF extraction via pdfjs-dist (text + embedded images)
// ---------------------------------------------------------------------------

const PDF_PAGE_TIMEOUT = 5000; // 5 seconds per page max

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)),
  ]);
}

async function parsePdf(buffer: ArrayBuffer): Promise<ParsedPage[]> {
  const doc = await pdfjs.getDocument({ data: new Uint8Array(buffer) }).promise;
  const pages: ParsedPage[] = [];

  for (let i = 1; i <= doc.numPages; i++) {
    try {
      const page = await withTimeout(doc.getPage(i), PDF_PAGE_TIMEOUT, `getPage(${i})`);

      // --- Text extraction with position-aware table reconstruction ---
      const content = await withTimeout(page.getTextContent(), PDF_PAGE_TIMEOUT, `getTextContent(${i})`);
      const { freeText, tables } = reconstructPdfPageText(content.items, i);

      const text = [freeText, tables].filter(Boolean).join('\n\n');

      // --- Image extraction via operator list (optional - may timeout) ---
      let images: ExtractedImage[] = [];
      try {
        images = await withTimeout(extractPdfPageImages(page, i), PDF_PAGE_TIMEOUT, `extractImages(${i})`);
      } catch {
        console.log('[parsePdf] Image extraction timed out or failed for page', i, '- continuing without images');
      }

      pages.push({ pageNumber: i, text, images: images.length > 0 ? images : undefined });
    } catch (pageErr) {
      console.error('[parsePdf] Failed to process page', i, ':', pageErr);
      // Add a placeholder page with error info so we don't lose the whole document
      pages.push({ pageNumber: i, text: `[Page ${i} could not be parsed - timeout or error]`, images: undefined });
    }
  }

  return pages;
}

/** Position-aware text reconstruction: detects tabular regions via Y/X clustering. */
function reconstructPdfPageText(items: any[], pageNum: number): { freeText: string; tables: string } {
  const Y_TOLERANCE = 3; // points — items within this range are on the same line
  const X_TOLERANCE = 10; // points — column positions must repeat within this range
  const MIN_TABLE_ROWS = 3; // minimum consecutive multi-cell rows to qualify as table
  const MIN_CELLS_PER_ROW = 2; // minimum items per row to be a potential table row

  // Extract positioned text items
  interface PosItem {
    str: string;
    x: number;
    y: number;
  }
  const posItems: PosItem[] = [];
  for (const item of items) {
    if (!('str' in item) || typeof item.str !== 'string' || !item.str.trim()) continue;
    const transform = (item as any).transform;
    if (!transform || !Array.isArray(transform) || transform.length < 6) {
      posItems.push({ str: item.str, x: 0, y: 0 });
      continue;
    }
    posItems.push({ str: item.str, x: transform[4] as number, y: transform[5] as number });
  }

  if (posItems.length === 0) return { freeText: '', tables: '' };

  // --- Group items by Y position (rows) ---
  // Sort by Y descending (top of page = larger Y in PDF coordinates), then X ascending
  const sorted = [...posItems].sort((a, b) => b.y - a.y || a.x - b.x);

  interface RowGroup {
    y: number;
    items: PosItem[];
  }
  const rows: RowGroup[] = [];
  let currentRow: RowGroup = { y: sorted[0]!.y, items: [sorted[0]!] };

  for (let i = 1; i < sorted.length; i++) {
    const item = sorted[i]!;
    if (Math.abs(item.y - currentRow.y) <= Y_TOLERANCE) {
      currentRow.items.push(item);
    } else {
      rows.push(currentRow);
      currentRow = { y: item.y, items: [item] };
    }
  }
  rows.push(currentRow);

  // Sort items within each row by X position
  for (const row of rows) {
    row.items.sort((a, b) => a.x - b.x);
  }

  // --- Detect table regions ---
  // A table is a consecutive run of rows where each row has ≥ MIN_CELLS_PER_ROW items
  // AND the X positions repeat across rows (column alignment)
  const tableRanges: { start: number; end: number }[] = [];
  let runStart = -1;

  for (let i = 0; i < rows.length; i++) {
    if (rows[i]!.items.length >= MIN_CELLS_PER_ROW) {
      if (runStart === -1) runStart = i;
    } else {
      if (runStart !== -1 && i - runStart >= MIN_TABLE_ROWS) {
        // Verify column alignment: check if X positions are consistent
        if (hasConsistentColumns(rows.slice(runStart, i), X_TOLERANCE)) {
          tableRanges.push({ start: runStart, end: i - 1 });
        }
      }
      runStart = -1;
    }
  }
  // Handle run that extends to end
  if (runStart !== -1 && rows.length - runStart >= MIN_TABLE_ROWS) {
    if (hasConsistentColumns(rows.slice(runStart), X_TOLERANCE)) {
      tableRanges.push({ start: runStart, end: rows.length - 1 });
    }
  }

  // --- Build output ---
  const tableRowIndices = new Set<number>();
  for (const range of tableRanges) {
    for (let i = range.start; i <= range.end; i++) tableRowIndices.add(i);
  }

  // Free text: non-table rows, join items with spaces
  const freeTextParts: string[] = [];
  for (let i = 0; i < rows.length; i++) {
    if (tableRowIndices.has(i)) continue;
    const text = rows[i]!.items.map(it => it.str).join(' ');
    if (text.trim()) freeTextParts.push(text.trim());
  }
  const freeText = freeTextParts.join(' ').replace(/\s+/g, ' ').trim();

  // Tables: format each range as pipe-separated
  const tableBlocks: string[] = [];
  for (const range of tableRanges) {
    // Determine column positions from all rows in this table
    const colPositions = detectColumnPositions(rows.slice(range.start, range.end + 1), X_TOLERANCE);

    const tableRows: string[][] = [];
    for (let i = range.start; i <= range.end; i++) {
      const row = rows[i]!;
      const cells: string[] = new Array(colPositions.length).fill('');
      for (const item of row.items) {
        // Assign item to nearest column
        let bestCol = 0;
        let bestDist = Math.abs(item.x - colPositions[0]!);
        for (let c = 1; c < colPositions.length; c++) {
          const dist = Math.abs(item.x - colPositions[c]!);
          if (dist < bestDist) {
            bestDist = dist;
            bestCol = c;
          }
        }
        cells[bestCol] = cells[bestCol] ? cells[bestCol] + ' ' + item.str : item.str;
      }
      tableRows.push(cells);
    }

    tableBlocks.push(`[Table on Page ${pageNum}:]\n${formatTableAsText(tableRows)}`);
  }

  return { freeText, tables: tableBlocks.join('\n\n') };
}

/** Check if a set of rows has consistent column positions (i.e., X values repeat). */
function hasConsistentColumns(rows: { items: { x: number }[] }[], tolerance: number): boolean {
  if (rows.length < 2) return false;

  // Collect all unique X positions from the first 3 rows (or fewer)
  const sampleRows = rows.slice(0, 3);
  const xPositions: number[][] = sampleRows.map(r => r.items.map(it => it.x));

  // For each X position in the first row, check if a similar X exists in other rows
  const refPositions = xPositions[0]!;
  let matchCount = 0;

  for (const refX of refPositions) {
    const matchesInOtherRows = xPositions
      .slice(1)
      .filter(rowXs => rowXs.some(x => Math.abs(x - refX) <= tolerance)).length;
    if (matchesInOtherRows === xPositions.length - 1) matchCount++;
  }

  // At least 2 columns must align across all sampled rows
  return matchCount >= 2;
}

/** Determine distinct column X positions from a range of rows. */
function detectColumnPositions(rows: { items: { x: number }[] }[], tolerance: number): number[] {
  // Collect all X values
  const allX: number[] = [];
  for (const row of rows) {
    for (const item of row.items) allX.push(item.x);
  }
  allX.sort((a, b) => a - b);

  // Cluster X values
  const clusters: number[] = [];
  for (const x of allX) {
    const existing = clusters.find(c => Math.abs(c - x) <= tolerance);
    if (!existing) clusters.push(x);
  }
  clusters.sort((a, b) => a - b);
  return clusters;
}

async function extractPdfPageImages(page: pdfjs.PDFPageProxy, pageNum: number): Promise<ExtractedImage[]> {
  const images: ExtractedImage[] = [];
  try {
    const ops = await page.getOperatorList();
    const seen = new Set<string>();

    for (let i = 0; i < ops.fnArray.length; i++) {
      const fn = ops.fnArray[i];
      if (fn !== pdfjs.OPS.paintImageXObject && fn !== (pdfjs.OPS as any).paintJpegXObject) continue;

      const imgName = (ops.argsArray[i] as string[])?.[0];
      if (!imgName || seen.has(imgName)) continue;
      seen.add(imgName);

      try {
        const imgData: any = await new Promise((resolve, reject) => {
          page.objs.get(imgName, (data: unknown) => (data ? resolve(data) : reject(new Error('no data'))));
        });

        const w = imgData.width as number;
        const h = imgData.height as number;
        if (!w || !h || w < MIN_IMAGE_DIMENSION || h < MIN_IMAGE_DIMENSION) continue;

        // Render to OffscreenCanvas → PNG base64
        const canvas = new OffscreenCanvas(w, h);
        const ctx = canvas.getContext('2d')!;
        const pixelData = new Uint8ClampedArray(imgData.data.buffer ?? imgData.data);
        ctx.putImageData(new ImageData(pixelData, w, h), 0, 0);
        const blob = await canvas.convertToBlob({ type: 'image/png' });
        const data = await blobToBase64(blob);

        images.push({ data, mediaType: 'image/png', context: `Page ${pageNum}`, width: w, height: h });
      } catch {
        /* skip unreadable images */
      }
    }
  } catch {
    /* getOperatorList can fail on some PDFs — degrade gracefully */
  }
  return images;
}

// ---------------------------------------------------------------------------
// XLSX extraction via SheetJS — structured with pipe separators + merge info
// ---------------------------------------------------------------------------

function parseXlsx(buffer: ArrayBuffer): ParsedPage[] {
  const workbook = XLSX.read(buffer, { type: 'array' });
  return workbook.SheetNames.map((name, i) => {
    const sheet = workbook.Sheets[name];
    if (!sheet) return { pageNumber: i + 1, text: `[Sheet: ${name}]\n(empty)` };

    // Use header: 1 to get raw row arrays, preserving column relationships
    const jsonData = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '' });

    // Build pipe-separated rows to preserve column structure
    const lines: string[] = [];
    for (const row of jsonData) {
      if (!Array.isArray(row)) continue;
      lines.push(row.map(cell => String(cell ?? '')).join(' | '));
    }

    // Annotate merged cells so the AI understands spanning relationships
    const merges = (sheet['!merges'] ?? []) as XLSX.Range[];
    let mergeInfo = '';
    if (merges.length > 0) {
      mergeInfo = '\n[Merged cells: ' + merges.map(m => XLSX.utils.encode_range(m)).join(', ') + ']';
    }

    return { pageNumber: i + 1, text: `[Sheet: ${name}]${mergeInfo}\n${lines.join('\n')}`.trim() };
  });
}

// ---------------------------------------------------------------------------
// DOCX extraction via mammoth (text) + JSZip (images)
// ---------------------------------------------------------------------------

async function parseDocx(buffer: ArrayBuffer): Promise<ParsedPage[]> {
  const result = await mammoth.extractRawText({ arrayBuffer: buffer });
  const text = result.value.trim();

  const zip = await JSZip.loadAsync(buffer);

  // Extract structured tables from word/document.xml
  const tableBlocks = await extractDocxTables(zip);

  // Extract embedded images from word/media/ via JSZip
  const images = await extractDocxImages(zip);

  const fullText = [text, tableBlocks].filter(Boolean).join('\n\n');

  // DOCX doesn't have "pages" — return as single block
  return [{ pageNumber: 1, text: fullText, images: images.length > 0 ? images : undefined }];
}

/** Extract structured tables from word/document.xml */
async function extractDocxTables(zip: JSZip): Promise<string> {
  try {
    const docFile = zip.files['word/document.xml'];
    if (!docFile) return '';
    const xml = await docFile.async('text');
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, 'application/xml');

    // Word uses w: prefix — try namespace-aware first, fall back to local name
    const WML_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
    let tables = doc.getElementsByTagNameNS(WML_NS, 'tbl');
    if (tables.length === 0) {
      // Fallback: some parsers strip namespaces
      tables = doc.getElementsByTagName('w:tbl');
    }
    if (tables.length === 0) return '';

    const blocks: string[] = [];
    for (let t = 0; t < tables.length; t++) {
      const tbl = tables[t]!;
      const rows: string[][] = [];

      // Get rows — try namespace-aware then fallback
      let trs = tbl.getElementsByTagNameNS(WML_NS, 'tr');
      if (trs.length === 0) trs = tbl.getElementsByTagName('w:tr');

      for (let r = 0; r < trs.length; r++) {
        const tr = trs[r]!;
        const cells: string[] = [];

        // Get cells
        let tcs: HTMLCollectionOf<Element>;
        tcs = tr.getElementsByTagNameNS(WML_NS, 'tc');
        if (tcs.length === 0) tcs = tr.getElementsByTagName('w:tc');

        for (let c = 0; c < tcs.length; c++) {
          const tc = tcs[c]!;

          // Check for horizontal merge (gridSpan)
          const gridSpanEl =
            tc.getElementsByTagNameNS(WML_NS, 'gridSpan')[0] ?? tc.getElementsByTagName('w:gridSpan')[0];
          const gridSpan = parseInt(gridSpanEl?.getAttribute('w:val') ?? gridSpanEl?.getAttribute('val') ?? '1', 10);

          // Collect all text runs within this cell
          let textNodes = tc.getElementsByTagNameNS(WML_NS, 't');
          if (textNodes.length === 0) textNodes = tc.getElementsByTagName('w:t');
          const textParts: string[] = [];
          for (let k = 0; k < textNodes.length; k++) {
            const txt = textNodes[k]?.textContent?.trim();
            if (txt) textParts.push(txt);
          }
          const cellText = textParts.join(' ');
          cells.push(cellText);
          for (let s = 1; s < gridSpan; s++) cells.push('');
        }
        if (cells.length > 0) rows.push(cells);
      }

      if (rows.length > 0) {
        blocks.push(`[Table:]\n${formatTableAsText(rows)}`);
      }
    }
    return blocks.join('\n\n');
  } catch {
    return ''; // degrade gracefully
  }
}

async function extractDocxImages(zip: JSZip): Promise<ExtractedImage[]> {
  const images: ExtractedImage[] = [];
  try {
    const mediaFiles = Object.keys(zip.files).filter(
      name => name.startsWith('word/media/') && SUPPORTED_IMAGE_EXTENSIONS.test(name),
    );

    for (const path of mediaFiles) {
      const file = zip.files[path];
      if (!file) continue;
      const data = await file.async('base64');
      const mediaType = extToMediaType(path);
      images.push({ data, mediaType, context: 'Document', width: 0, height: 0 });
    }
  } catch {
    /* degrade gracefully if ZIP parsing fails */
  }
  return images;
}

// ---------------------------------------------------------------------------
// PPTX extraction via JSZip + DOMParser (text + images per slide)
// ---------------------------------------------------------------------------

async function parsePptx(buffer: ArrayBuffer): Promise<ParsedPage[]> {
  const zip = await JSZip.loadAsync(buffer);
  const pages: ParsedPage[] = [];

  // Slides are at ppt/slides/slide1.xml, slide2.xml, …
  const slideFiles = Object.keys(zip.files)
    .filter(name => /^ppt\/slides\/slide\d+\.xml$/i.test(name))
    .sort((a, b) => {
      const numA = parseInt(a.match(/slide(\d+)/i)?.[1] ?? '0', 10);
      const numB = parseInt(b.match(/slide(\d+)/i)?.[1] ?? '0', 10);
      return numA - numB;
    });

  const parser = new DOMParser();

  for (let i = 0; i < slideFiles.length; i++) {
    const slideFile = slideFiles[i]!;
    const xml = await zip.files[slideFile]!.async('text');
    const doc = parser.parseFromString(xml, 'application/xml');

    // --- Collect all nodes that belong to tables so we can exclude them from free text ---
    const tableNodeSet = new Set<Node>();
    const tables = doc.getElementsByTagNameNS(DRAWINGML_NS, 'tbl');
    for (let t = 0; t < tables.length; t++) {
      const tblEl = tables[t]!;
      // Mark every descendant <a:t> inside a table
      const innerT = tblEl.getElementsByTagNameNS(DRAWINGML_NS, 't');
      for (let k = 0; k < innerT.length; k++) tableNodeSet.add(innerT[k]!);
    }

    // --- Extract free text (excluding table text) ---
    const textNodes = doc.getElementsByTagNameNS(DRAWINGML_NS, 't');
    const texts: string[] = [];
    for (let j = 0; j < textNodes.length; j++) {
      const node = textNodes[j]!;
      if (tableNodeSet.has(node)) continue; // skip table text
      const t = node.textContent?.trim();
      if (t) texts.push(t);
    }

    // --- Extract structured tables ---
    const tableBlocks = extractPptxSlideTables(doc, i + 1);

    const fullText = [texts.join(' '), tableBlocks].filter(Boolean).join('\n\n');

    // Extract images referenced by this slide
    const images = await extractPptxSlideImages(zip, i, slideFile);

    pages.push({ pageNumber: i + 1, text: fullText, images: images.length > 0 ? images : undefined });
  }

  return pages;
}

/** Extract structured tables from a PPTX slide XML document. */
function extractPptxSlideTables(doc: Document, slideNum: number): string {
  const tables = doc.getElementsByTagNameNS(DRAWINGML_NS, 'tbl');
  if (tables.length === 0) return '';

  const blocks: string[] = [];
  for (let t = 0; t < tables.length; t++) {
    const tbl = tables[t]!;
    const trs = tbl.getElementsByTagNameNS(DRAWINGML_NS, 'tr');
    const rows: string[][] = [];

    for (let r = 0; r < trs.length; r++) {
      const tr = trs[r]!;
      // Only direct <a:tc> children (not nested tables)
      const cells: string[] = [];
      for (let c = 0; c < tr.childNodes.length; c++) {
        const child = tr.childNodes[c];
        if (!child || child.nodeType !== 1) continue; // element nodes only
        const el = child as Element;
        if (el.localName !== 'tc') continue;

        // Handle gridSpan (horizontally merged cells)
        const gridSpan = parseInt(el.getAttribute('gridSpan') ?? '1', 10);
        const textParts: string[] = [];
        const tNodes = el.getElementsByTagNameNS(DRAWINGML_NS, 't');
        for (let k = 0; k < tNodes.length; k++) {
          const txt = tNodes[k]?.textContent?.trim();
          if (txt) textParts.push(txt);
        }
        const cellText = textParts.join(' ');
        cells.push(cellText);
        // Fill spanned columns with empty marker
        for (let s = 1; s < gridSpan; s++) cells.push('');
      }
      if (cells.length > 0) rows.push(cells);
    }

    if (rows.length > 0) {
      blocks.push(`[Table on Slide ${slideNum}:]\n${formatTableAsText(rows)}`);
    }
  }
  return blocks.join('\n\n');
}

async function extractPptxSlideImages(zip: JSZip, slideIndex: number, slideFile: string): Promise<ExtractedImage[]> {
  const images: ExtractedImage[] = [];
  try {
    // Relationship file maps slide content to media files
    const relsPath = slideFile.replace('ppt/slides/', 'ppt/slides/_rels/') + '.rels';
    const relsFile = zip.files[relsPath];
    if (!relsFile) return images;

    const relsXml = await relsFile.async('text');
    const parser = new DOMParser();
    const doc = parser.parseFromString(relsXml, 'application/xml');
    const rels = doc.getElementsByTagName('Relationship');

    for (let i = 0; i < rels.length; i++) {
      const target = rels[i]?.getAttribute('Target');
      const type = rels[i]?.getAttribute('Type');
      if (!target || !type?.includes('/image')) continue;

      // Resolve relative path: ../media/image1.png → ppt/media/image1.png
      const imgPath = target.startsWith('../') ? 'ppt/' + target.slice(3) : target;
      if (!SUPPORTED_IMAGE_EXTENSIONS.test(imgPath)) continue;

      const imgFile = zip.files[imgPath];
      if (!imgFile) continue;

      const data = await imgFile.async('base64');
      const mediaType = extToMediaType(imgPath);
      images.push({ data, mediaType, context: `Slide ${slideIndex + 1}`, width: 0, height: 0 });
    }
  } catch {
    /* degrade gracefully */
  }
  return images;
}

// ---------------------------------------------------------------------------
// Image utility functions
// ---------------------------------------------------------------------------

function extToMediaType(path: string): ExtractedImage['mediaType'] {
  const ext = path.split('.').pop()?.toLowerCase();
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'gif') return 'image/gif';
  if (ext === 'webp') return 'image/webp';
  return 'image/png';
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

function base64ToBlob(data: string, type: string): Blob {
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type });
}

/**
 * Enforce the per-document image budget:
 * - Resolve unknown dimensions via createImageBitmap
 * - Drop images smaller than MIN_IMAGE_DIMENSION
 * - Resize images larger than MAX_IMAGE_DIMENSION
 * - Keep only the MAX_IMAGES_PER_DOC largest images
 */
async function enforceImageBudget(pages: ParsedPage[]): Promise<void> {
  // Collect all images across pages
  const allImages: { page: ParsedPage; index: number; img: ExtractedImage }[] = [];
  for (const page of pages) {
    if (!page.images) continue;
    for (let i = 0; i < page.images.length; i++) {
      allImages.push({ page, index: i, img: page.images[i]! });
    }
  }

  if (allImages.length === 0) return;

  // Resolve dimensions and resize oversized images
  for (const entry of allImages) {
    try {
      const blob = base64ToBlob(entry.img.data, entry.img.mediaType);
      const bitmap = await createImageBitmap(blob);
      entry.img.width = bitmap.width;
      entry.img.height = bitmap.height;
      bitmap.close();

      // Skip tiny images
      if (entry.img.width < MIN_IMAGE_DIMENSION || entry.img.height < MIN_IMAGE_DIMENSION) {
        entry.img.data = ''; // Mark for removal
        continue;
      }

      // Resize if too large
      if (entry.img.width > MAX_IMAGE_DIMENSION || entry.img.height > MAX_IMAGE_DIMENSION) {
        const scale = MAX_IMAGE_DIMENSION / Math.max(entry.img.width, entry.img.height);
        const w = Math.round(entry.img.width * scale);
        const h = Math.round(entry.img.height * scale);
        const resizeBitmap = await createImageBitmap(base64ToBlob(entry.img.data, entry.img.mediaType));
        const canvas = new OffscreenCanvas(w, h);
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(resizeBitmap, 0, 0, w, h);
        resizeBitmap.close();
        const resizedBlob = await canvas.convertToBlob({ type: 'image/png' });
        entry.img.data = await blobToBase64(resizedBlob);
        entry.img.mediaType = 'image/png';
        entry.img.width = w;
        entry.img.height = h;
      }
    } catch {
      entry.img.data = ''; // Mark for removal on error
    }
  }

  // Sort by pixel area descending (largest = most likely to contain useful info)
  const validImages = allImages.filter(e => e.img.data.length > 0);
  validImages.sort((a, b) => b.img.width * b.img.height - a.img.width * a.img.height);

  // Keep only top MAX_IMAGES_PER_DOC — mark the rest for removal
  const keep = new Set(validImages.slice(0, MAX_IMAGES_PER_DOC).map(e => e.img));

  // Rebuild page images arrays
  for (const page of pages) {
    if (!page.images) continue;
    page.images = page.images.filter(img => keep.has(img));
    if (page.images.length === 0) page.images = undefined;
  }
}
